import $ from 'jquery';

import { HEADER_DISY_CANCEL_ID, HEADER_DISY_PROBLEM } from '@disy/lib.backend/api-constants';
import { isBackendAbortSignal } from '@disy/lib.backend/backend-abort-controller';
import { cadenzaUrl } from '@disy/lib.backend/cadenza-url';
import type { AbortError } from '@disy/lib/abort-error';
import { createAbortError, isAbortError } from '@disy/lib/abort-error';
import { decodeBase64 } from '@disy/lib/base-64';
import { assert } from '@disy/lib/custom-error';
import { logger } from '@disy/lib/logging';
import { extendUrlParams, getUrlParams } from '@disy/lib/url-param-util';

import { errorAlert } from 'cadenza/alert';
import type { Problem } from 'cadenza/api-client/dto/problem-dto';

import i18n from './messages.properties';

interface AjaxRequestParams {
  [key: string]: unknown;
}

export interface BackendException {
  key: string;
  title: string;
  type: string;
  status: number;
}

const UNKNOWN_SERVER_ERROR_ERROR_KEY = 'unknown-server-error';

/**
 * Allows to remember default ajax request data property via ajaxSetup function.
 */
let defaultAjaxRequestParams: object = {};
/**
 * Cache setting that we can apply to all ajax requests
 */
let cache = true;

/**
 * Wrapper around $.ajax() for requesting our REST endpoints.
 * It makes requests cancelable via the provided signal promise.
 * It returns an ES6 promise that resolves to the parsed JSON result of the request in case of a
 * successful request and fails if the request results in an error. A detailed error object is
 * provided if it was available in the response body.
 * If the request failed because audit logging was unavailable, additionally an error alert with a
 * purposely vague message is displayed.
 *
 * @param url - The URL to request
 * @param settings - Request settings, see https://api.jquery.com/jquery.ajax/#jQuery-ajax-settings
 * @param queryPostProcessor - A callback that is called after the request successfully completes, before resolving the promise.
 * Allows to perform additional operations directly on the jqHr object, such as reading specific headers,
 * without changing the return type of the ajax() function.
 * @return Result promise.
 */
export function ajax<R>(
  url: string,
  settings: JQuery.AjaxSettings & { signal?: AbortSignal } = {},
  queryPostProcessor?: (jqXhr: JQuery.jqXHR) => void,
) {
  if (settings.signal) {
    assert(!settings.signal.aborted, 'Settings signal must not be aborted, already');
  }

  url = fillUrlWithDefaultParams(url);

  // for backend cancelable jobs (work orders)
  if (isBackendAbortSignal(settings.signal)) {
    settings.headers = Object.assign(settings.headers || {}, { [HEADER_DISY_CANCEL_ID]: settings.signal.id });
  }

  const jqXhr = $.ajax(cadenzaUrl(url), settings);

  if (settings.signal) {
    settings.signal.addEventListener('abort', () => jqXhr.abort());
  }

  return new Promise<R>((resolve, reject) => {
    jqXhr.then(
      // success
      (result) => {
        if (settings.signal?.aborted) {
          // Sometimes it can happen that the request was cancelled, but still managed to be executed.
          // Manually abort the results in such case.
          // Otherwise it can lead to a wrong state if we cancel the request, but still get its results.
          throw createAbortError();
        }
        if (isBackendAbortSignal(settings.signal)) {
          settings.signal.finished = true;
        }
        queryPostProcessor?.(jqXhr);
        resolve(result);
      },
      // fail
      () => {
        defaultXhrErrorHandler(jqXhr, reject);
      },
    );
  }).finally(() => {
    if (settings.signal) {
      settings.signal.removeEventListener('abort', () => jqXhr.abort());
    }
  });
}

/**
 * Executes a backend REST request and returns both the response data and specified response headers.
 *
 * This function extends the standard `ajax()` functionality by allowing you to retrieve specific HTTP
 * headers from the response alongside the main response body. It's useful when the server
 * sends important metadata or state information via custom headers.
 *
 * Header values are expected to be JSON strings encoded in Base64 to ensure proper transmission of
 * non-ASCII characters in HTTP headers. The function automatically decodes and parses these values.
 *
 * If a requested header is not present in the response, its value will be `undefined` in the
 * returned object.
 *
 * @example
 * // Fetch data and the total count header from a paginated API
 * const result = await fetchWithResponseHeaders<Feature[], { 'X-Warning-Messages': WarningMessagesDto }>(
 *   '/api/features',
 *   ['X-Total-Count']
 * );
 *
 * // Access the response data
 * const features = result.response;
 *
 * // Access the header value (with type safety)
 * const messages = result.responseHeaders['X-Warning-Messages'];
 *
 * @typeParam R - Type of the expected response data
 * @typeParam H - Type of the headers to extract, as a record with header names as keys
 * @param url - The URL to request
 * @param responseHeaders - Array of HTTP header names to extract from the response, must match the
 * keys in the `H` type
 * @param settings - Request settings, see https://api.jquery.com/jquery.ajax/#jQuery-ajax-settings
 * @returns Promise resolving to an object containing both the response data and the extracted headers.
 * There's no guarantee that all requested headers will be present so the type for the headers object is `Partial<R>`.
 */
export async function fetchWithResponseHeaders<R, H extends Record<string, unknown>>(
  url: string,
  responseHeaders: Array<string & keyof H>,
  settings: JQuery.AjaxSettings & { signal?: AbortSignal } = {},
): Promise<{ response: R; responseHeaders: Partial<H> }> {
  const responseHeaderValues: Partial<H> = {};
  const mainResponse = await ajax<R>(url, settings, (jqXhr) => {
    for (const responseHeaderName of responseHeaders) {
      responseHeaderValues[responseHeaderName] = extractHeaderValue(jqXhr, responseHeaderName);
    }
  });

  return {
    response: mainResponse,
    responseHeaders: responseHeaderValues,
  };
}

/**
 * Header values are JSON string that are encoded as Base64 in order to respect the restrictions for HTTP headers (only ASCII characters)
 */
function extractHeaderValue(jqXhr: JQuery.jqXHR, headerName: string) {
  const rawHeaderValue = jqXhr.getResponseHeader(headerName);
  if (rawHeaderValue) {
    try {
      return JSON.parse(decodeBase64(rawHeaderValue));
    } catch (error) {
      throw new Error(
        `Failed to parse HTTP response header as Base64 encoded JSON. Header name: "${headerName}", header value: "${rawHeaderValue}"`,
        { cause: error },
      );
    }
  }
}

/**
 * Sets the default url parameters for every subsequent ajax() call. If ajax() has data passed
 * with the same properties as defaults, they will be overridden for that single request. To reset
 * the defaults, pass an empty object here.
 *
 * This function is analogical to jQuery's ajaxSetup(): https://api.jquery.com/jquery.ajaxsetup/
 * but operates only on url parameters
 *
 * @param defaultParams - Default url parameters (in map-like js object) for each next ajax() call.
 */
export function ajaxSetup(defaultParams: AjaxRequestParams) {
  defaultAjaxRequestParams = Object.assign({}, defaultParams);
}

export function disableCache(disable: boolean) {
  cache = !disable;
}

export function fillUrlWithDefaultParams(url: string) {
  if (!defaultAjaxRequestParams) {
    return url;
  }
  const urlWithoutParams = url.split('?')[0];
  return extendUrlParams(urlWithoutParams, {
    ...defaultAjaxRequestParams,
    ...getUrlParams(url),
    // simulates what JQuery does
    ...(cache ? {} : { _: Date.now() }),
  });
}

export async function defaultXhrErrorHandler(
  xhr: JQuery.jqXHR | XMLHttpRequest,
  errorConsumer: (error: unknown) => void,
) {
  try {
    const problem = await extractProblemFromXhr(xhr);
    alertIfAuditLogError(problem);
    errorConsumer(problem);
  } catch (error) {
    if (!isAbortError(error)) {
      logger.error('Unable to handle server error', error);
      errorConsumer(error);
    }
  }
}

/**
 * Builds an error descriptor object given a failed XMLHttpRequest or special jQuery XHR object.
 *
 * @param  xhr - the XMLHttpRequest or jqXHR wrapper
 * @return The error object
 */
async function extractProblemFromXhr(xhr: XMLHttpRequest | JQuery.jqXHR): Promise<ResponseProblem> {
  // request has been canceled in client
  if (xhr.statusText === 'abort') {
    // statusText 'abort' set automatically by jqXhr.abort
    return createAbortError();
  }

  let responseProblem;

  // attempt to read xhr.responseText when not present might cause an error in some cases
  let responseText = null;
  try {
    responseText = xhr.responseText;
    // eslint-disable-next-line no-empty
  } catch {}

  const errorHeaderValue = xhr.getResponseHeader(HEADER_DISY_PROBLEM);
  if (errorHeaderValue) {
    // if error header is present, use the value
    responseProblem = JSON.parse(atob(errorHeaderValue));
  } else if ('responseJSON' in xhr) {
    // jqXHR response body
    responseProblem = xhr.responseJSON;
  } else if (responseText) {
    // normal XHR response body
    try {
      responseProblem = JSON.parse(xhr.responseText);
    } catch {
      logger.error('The error message does not seem to be JSON: ', xhr.responseText);
      // In case the proxy (e.g. Nginx) intercepts the request, it may not produce a JSON response.
      responseProblem = {
        key: UNKNOWN_SERVER_ERROR_ERROR_KEY,
        type: `http://disy.net/problem/${UNKNOWN_SERVER_ERROR_ERROR_KEY}`,
        title: 'An unknown error occurred.',
      };
    }
  } else if ('responseType' in xhr && xhr.responseType === 'blob') {
    // special blob request case, we have to parse the downloaded file (returns a promise)
    return await extractErrorFromXhrBlobResponse(xhr);
  }

  if (responseProblem?.type?.startsWith('http://disy.net/problem/')) {
    // NOSONAR "untrusted source" is only a namespace
    return Object.assign(responseProblem, { status: xhr.status });
  }

  return { status: xhr.status, content: ('responseJSON' in xhr && xhr.responseJSON) || xhr.responseText };
}

export interface ContentWithStatusResponse {
  content?: object | string;
  status?: number;
}

export type ResponseProblem = AbortError | Problem | ContentWithStatusResponse;

export function isContentWithStatusResponse(error: unknown): error is ContentWithStatusResponse {
  const keys = Object.keys(error as never);
  return keys.includes('status') || keys.includes('content');
}

function extractErrorFromXhrBlobResponse(xhr: XMLHttpRequest): Promise<ResponseProblem> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function () {
      const errorDetails = JSON.parse(reader.result as string);
      errorDetails.status = xhr.status;
      resolve(errorDetails);
    };
    reader.onerror = reject;
    reader.readAsText(xhr.response);
  });
}

/*
 * Creates a new EventSource for the given relative url.
 * @param url Relative Cadenza url to the event source endpoint
 * @returns {EventSource}
 */
export function createEventSource(url: string) {
  return new EventSource(cadenzaUrl(url));
}

export function encodePathVariable(value: string) {
  //  CADENZA-14580 - To handle path params which contain '/' and make it possible to handle
  //  those values correctly by UrlPathHelper we'll encode those parameters as URIComponents and then with Base64 and
  //  remove the trailing padding '=' characters. Encoding only with Base64 would cause problems with handling umlauts and other unicode characters.
  //  The reason for removing padding characters is described in CADENZA-12272.
  return encodeToBase64WithoutPadding(encodeURIComponent(value));
}

const AUDIT_ERROR_KEY = 'audit-log-failed';

function alertIfAuditLogError(problem: ResponseProblem) {
  if (isAuditLogError(problem)) {
    errorAlert(i18n('api.errors.auditLogFailed.title'), i18n('api.errors.auditLogFailed.text'));
  }
}

function isAuditLogError(problem: ResponseProblem) {
  return (problem as Problem).key === AUDIT_ERROR_KEY;
}

function encodeToBase64WithoutPadding(param: string) {
  return btoa(param).replace(/=+$/, '');
}

export function setArrayParamAsRepeatedParam(
  searchParams: URLSearchParams,
  paramName: string,
  arrayValue: unknown[],
  serializeToJson = false,
) {
  arrayValue.forEach((value) =>
    searchParams.append(paramName, serializeToJson ? JSON.stringify(value) : String(value)),
  );
}

/** executes the callback sequentially (next call is waiting for the first one to complete) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sequentialExecution<T extends (...args: any[]) => Promise<any>>(callback: T) {
  let currentPromise: Promise<unknown> | undefined;
  return function (this: unknown, ...args: Parameters<T>) {
    currentPromise = Promise.allSettled([currentPromise])
      .then(() => callback.apply(this, args))
      .finally(() => {
        currentPromise = undefined;
      });
    return currentPromise as ReturnType<T>;
  };
}

/**
 * Utility function that tries to parse and create Date objects from the string value of the
 * specified fields of the given object.
 * If there is no value for the given field, nothing is done.
 * If there is a value but the parsing fails, an error is thrown.
 *
 * The purpose of this function is to call it on DTO objects received from the backend before they
 * are returned to the application code.
 *
 * @param dto - An object
 * @param dateFieldNames - Name of the properties that the function will attempt to parse and replace with Date objects.
 */
export function deserializeDateFields(dto: object, dateFieldNames: string[]) {
  const record = dto as Record<string, unknown>; // allow accessing properties dynamically
  dateFieldNames.forEach((dateFieldName) => {
    const fieldValue = record[dateFieldName];
    if (fieldValue && typeof fieldValue === 'string') {
      try {
        record[dateFieldName] = new Date(fieldValue as string);
      } catch (error) {
        throw new Error(`Failed to parse date for field '${dateFieldName}'. Error: ${error}`);
      }
    }
  });
}
